/* Global WS listener for doorbell rings. Mounted in App.js so an incoming
   call pops the dialog regardless of which route the resident is on.
   Also fires an OS-level browser notification (via the existing Web
   Notification API permission) so a backgrounded/muted tab still surfaces
   the ring — chime alone is unreliable when the tab is unfocused.

   Also consumes the `?scan2call_ring=<id>` query param — set by the
   service worker when the resident taps a push notification while the
   app is closed. That flow rehydrates the ring via
   `GET /api/doorbell/rings/:id/incoming` and pops the same dialog.

   Poll fallback: WebSockets sometimes die silently on flaky mobile
   networks / aggressive Doze / Cloudflare edge failovers. To keep
   Scan2Call reliable, we ALSO poll `GET /doorbell/rings/mine/active`
   every 6 s. If a fresh ring shows up that the WS never delivered,
   we hydrate the same dialog. Cheap query — indexed on (user_id,
   status, started_at) — and only runs while the resident is signed
   in and no dialog is already open. */
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { useWebSocket } from "../../contexts/WebSocketContext";
import { showDoorbellNotification } from "../../utils/browserNotification";
import DoorbellRingDialog from "./DoorbellRingDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Cadence of the WS-fallback poll. Tuned to balance:
//   • responsiveness — visitor scans QR, resident sees dialog within one
//     tick even if WS dropped;
//   • server load — one cheap indexed find per resident every 6 s;
//   • battery — kept above 5 s so mobile Chrome doesn't throttle.
const ACTIVE_RING_POLL_MS = 6000;

export default function DoorbellListener() {
  const { user } = useAuth();
  const [incoming, setIncoming] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  // Latest known incoming ring id, kept in a ref so the poll can dedupe
  // without triggering re-renders (setIncoming already re-renders when
  // the ring genuinely changes).
  const currentRingIdRef = useRef(null);
  // Call-waiting slot — a SECOND visitor ringing while a ring/call is
  // already being handled. One slot only (like phone call waiting);
  // further rings are ignored until the slot frees up.
  const [waiting, setWaiting] = useState(null);
  const waitingRef = useRef(null);
  // Set when the resident taps "End & answer" — the promoted ring's
  // dialog should auto-answer on mount.
  const [autoAnswer, setAutoAnswer] = useState(false);

  const stashWaiting = (ringMsg) => {
    if (!ringMsg?.ring_id) return;
    if (ringMsg.ring_id === currentRingIdRef.current) return;
    if (waitingRef.current) return; // slot taken — first waiter keeps priority
    waitingRef.current = ringMsg.ring_id;
    setWaiting(ringMsg);
  };

  const clearWaiting = () => {
    waitingRef.current = null;
    setWaiting(null);
  };

  // Waiting rings expire client-side too (75 s > the 60 s ring window)
  // in case the visitor's end-event never reaches us.
  useEffect(() => {
    if (!waiting) return undefined;
    const id = setTimeout(clearWaiting, 75000);
    return () => clearTimeout(id);
  }, [waiting]);

  // Promote the waiting ring into the main dialog. Called after the
  // current leg has been ended (see DoorbellRingDialog.handleSwitch).
  const promoteWaiting = () => {
    const next = waitingRef.current ? waiting : null;
    clearWaiting();
    if (!next) return;
    currentRingIdRef.current = next.ring_id;
    setAutoAnswer(true);
    setIncoming(next);
  };

  const declineWaiting = async () => {
    const next = waitingRef.current ? waiting : null;
    clearWaiting();
    if (!next) return;
    // Resident-authenticated /end = a conscious decline (no missed-call
    // push, matching the main Decline button's behaviour).
    try { await axios.post(`${API}/doorbell/ring/${next.ring_id}/end`, {}, { withCredentials: true }); } catch { /* already ended */ }
  };

  // Permission prompt intentionally removed from mount — Android Chrome
  // silently blocks `Notification.requestPermission()` when it isn't
  // tied to a transient user activation. The prompt is now surfaced
  // via a click-bound "Enable ring notifications" button on
  // <DoorbellCard/>. See doorbell/DoorbellCard.js.

  useWebSocket((msg) => {
    // A ring ending elsewhere (visitor gave up / voicemail) frees the
    // call-waiting slot.
    if (msg?.type === "doorbell_end" && msg.ring_id && msg.ring_id === waitingRef.current) {
      clearWaiting();
      return;
    }
    if (msg?.type !== "doorbell_ring") return;
    if (!user?.user_id || msg.user_id !== user.user_id) return;
    if (msg.ring_id === currentRingIdRef.current) return; // duplicate delivery
    if (currentRingIdRef.current) {
      // Already ringing/on a call → call waiting, not a dialog swap.
      // (Swapping the live dialog was the old behaviour and it hijacked
      // in-progress calls.)

      console.info("[Scan2Call] WS second ring → call waiting", msg.ring_id);
      stashWaiting(msg);
      return;
    }

    console.info("[Scan2Call] WS doorbell_ring →", msg.ring_id, "from", msg.visitor_name || "Visitor");
    currentRingIdRef.current = msg.ring_id;
    setAutoAnswer(false);
    setIncoming(msg);
    showDoorbellNotification(msg, () => { /* tab focus handled in helper */ });
  });

  // Poll fallback: fires every ACTIVE_RING_POLL_MS while signed in AND
  // no dialog is already showing. If it finds a ringing call the WS
  // missed, hydrate the same DoorbellRingDialog transparently.
  useEffect(() => {
    if (!user?.user_id) return undefined;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const r = await axios.get(`${API}/doorbell/rings/mine/active`, { withCredentials: true });
        const ring = r.data?.ring;
        if (!ring || cancelled) return;
        if (currentRingIdRef.current === ring.ring_id) return;
        if (currentRingIdRef.current) {
          // Second ring the WS missed → call-waiting slot.
          stashWaiting(ring);
          return;
        }

        console.info("[Scan2Call] poll picked up missed ring →", ring.ring_id);
        currentRingIdRef.current = ring.ring_id;
        setAutoAnswer(false);
        setIncoming(ring);
        showDoorbellNotification(ring, () => {});
      } catch { /* silent — poll retries next tick */ }
    };
    // Kick off immediately so a page reload while ringing doesn't wait 6s.
    tick();
    const id = setInterval(tick, ACTIVE_RING_POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [user?.user_id]);

  // Service-worker `message` channel — third independent delivery path
  // for a ring. When the browser handles a push notification for a
  // Scan2Call ring, the SW `push` handler (see public/service-worker.js)
  // matches all open windows and postMessages the payload here.
  //
  // Why we need this on top of WS + poll:
  //   • WS can drop silently on mobile power-save / edge failover.
  //   • Poll waits up to 6 s, which is a noticeable delay for a ring.
  //   • Push, however, fires even when the tab is backgrounded and
  //     even when the socket has just been killed by the OS.
  //
  // Deduped through `currentRingIdRef` so we don't re-fetch and reset
  // the dialog if the same ring already arrived via WS or poll a
  // millisecond earlier.
  useEffect(() => {
    if (!user?.user_id) return undefined;
    if (typeof navigator === "undefined" || !navigator.serviceWorker) return undefined;
    const handler = async (event) => {
      const data = event?.data;
      if (!data || data.type !== "doorbell_ring") return;
      const ringId = data.ring_id;
      if (!ringId) return;
      if (currentRingIdRef.current === ringId) return; // already handled
      // eslint-disable-next-line no-console
      console.info("[Scan2Call] SW message doorbell_ring →", ringId, "from", data.visitor_name || "Visitor");
      try {
        const r = await axios.get(`${API}/doorbell/rings/${ringId}/incoming`, { withCredentials: true });
        if (currentRingIdRef.current === ringId) return; // WS/poll raced us
        if (currentRingIdRef.current) {
          stashWaiting(r.data);
          return;
        }
        currentRingIdRef.current = ringId;
        setAutoAnswer(false);
        setIncoming(r.data);
        showDoorbellNotification(r.data, () => {});
      } catch { /* ring already ended by the time we fetched — silent */ }
    };
    navigator.serviceWorker.addEventListener("message", handler);
    return () => navigator.serviceWorker.removeEventListener("message", handler);
  }, [user?.user_id]);

  // Push-notification deep-link: the service worker sets
  // `?scan2call_ring=<ring_id>` when the resident taps "Answer" (or
  // just the notification body) on an incoming Scan2Call ring while
  // the app was closed. We fetch a fresh Agora payload for that ring
  // and open the same dialog the WS path would have opened. Silently
  // strip the param on success/failure so a browser refresh doesn't
  // re-trigger.
  useEffect(() => {
    if (!user?.user_id) return;
    const params = new URLSearchParams(location.search);
    const ringId = params.get("scan2call_ring");
    if (!ringId) return;
    // Strip the param immediately so a refresh doesn't loop.
    params.delete("scan2call_ring");
    navigate({ search: params.toString() ? `?${params.toString()}` : "" }, { replace: true });

    (async () => {
      try {
        const r = await axios.get(`${API}/doorbell/rings/${ringId}/incoming`, { withCredentials: true });
        if (currentRingIdRef.current && currentRingIdRef.current !== ringId) {
          // Tapped a second ring's notification while already on a
          // call → call-waiting slot, don't hijack the live dialog.
          stashWaiting(r.data);
          return;
        }
        currentRingIdRef.current = ringId;
        setAutoAnswer(false);
        setIncoming(r.data);
      } catch (e) {
        const detail = e?.response?.data?.detail || "";
        if (e?.response?.status === 410) toast(detail || "That call has already ended.");
        // 404 → not our ring / already closed; stay silent.
      }
    })();
  }, [user?.user_id, location.search, navigate]);

  if (!incoming) return null;
  return (
    <DoorbellRingDialog
      key={incoming.ring_id}
      ring={incoming}
      autoAnswer={autoAnswer}
      waitingRing={waiting}
      onSwitchToWaiting={promoteWaiting}
      onDeclineWaiting={declineWaiting}
      onClose={() => {
        currentRingIdRef.current = null;
        setAutoAnswer(false);
        setIncoming(null);
      }}
    />
  );
}
